import {
  CameraAnimation,
  CameraAnimationData,
} from "@/components/r3f/animations/camera-animation";
import { Camera } from "@react-three/fiber";
import { useCallback, useState, type JSX } from "react";
import { PerspectiveCamera } from "three";

export type CameraAnimationReturn = {
  /** Animation element that should be rendered by the using component. Is null when no animation plays */
  animationToRender: JSX.Element | null;

  /** Function to start an animation */
  startAnimation(
    from: PerspectiveCamera,
    to: CameraAnimationData,
    onAnimationFinished?: () => void,
  ): void;

  /** The camera used for animation. Should be enabled for rendering by the using component. */
  animationCamera: Camera;
};

/**
 * @returns a hook to manage a single camera animation
 */
export function useCameraAnimation(): CameraAnimationReturn {
  const [animationCamera, setAnimationCamera] = useState<Camera>(
    new PerspectiveCamera(),
  );
  const [activeAnimation, setActiveAnimation] = useState<CameraAnimationData>();
  const [onAnimationFinished, setOnAnimationFinished] = useState<
    (() => void) | undefined
  >(undefined);

  const startAnimation = useCallback(
    (
      from: Camera,
      to: CameraAnimationData,
      onAnimationFinished?: () => void,
    ) => {
      setAnimationCamera(from.clone());
      setOnAnimationFinished(() => onAnimationFinished);
      setActiveAnimation(to);
    },
    [],
  );

  const animationToRender = activeAnimation ? (
    <CameraAnimation
      camera={animationCamera}
      onAnimationFinished={() => {
        setActiveAnimation(undefined);
        if (onAnimationFinished) {
          onAnimationFinished();
        }
        setOnAnimationFinished(undefined);
      }}
      {...activeAnimation}
    />
  ) : null;

  return { animationToRender, startAnimation, animationCamera };
}
